/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { BuilderPermission, JobContractStartBasedOnType } from '@gh/builder-shared-data';
import {
  isEntityInvalid,
  PlainImmutablePageState,
  setEntityField,
  setEntityFields,
  UserContext,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { isNotNil } from '@gh/core-util';
import { map$, switchMap$ } from '@gh/rx/operators';

import { find, first, isNil } from 'lodash';

import {
  CpStage,
  cpStageArr,
  CpStageCc,
  cpStageCcArr,
  cpStageToRemoved,
  cpStageToRestored,
  CpUnusedCc,
  cpUnusedCcArr,
  cpUnusedCcFromStageCc,
  cpUnusedCcToStageCc,
  CRITICAL_PATH_DETAILS_INIT,
  CriticalPathDetails,
  CriticalPathSelection,
  getCpStageCcId,
  getCpStageId,
  getCpUnusedCcId,
  setCpStageFields,
} from '../../resources/scheduling-cp-details.model';

const queryCpVisibleStages = (details: CriticalPathDetails) =>
  details.selection.showDeletedStages
    ? details.stages.filter((stage) => !stage.isDeleted)
    : details.stages.filter((stage) => stage.isActive);

@Injectable()
export class SchedulingCpDetailsFormState extends PlainImmutablePageState<CriticalPathDetails> {
  contractStartBasedOn$ = this.select(({ contractStartBasedOn }) => contractStartBasedOn);
  stages$ = this.select(({ stages }) => stages);
  selection$ = this.select(({ selection }) => selection);
  focusedCell$ = this.select(({ selection: { focusedCell } }) => focusedCell);
  showDeleted$ = this.selection$.pipe(map$(({ showDeletedStages }) => showDeletedStages));
  currentStage$ = this.selection$.pipe(
    switchMap$(({ selectedStageId }) =>
      this.stages$.pipe(map$((stages) => cpStageArr.find(stages, selectedStageId)))));
  visibleStages$ = this.showDeleted$.pipe(
    switchMap$(() => this.select(queryCpVisibleStages)));
  currentStageCostCenters$ = this.currentStage$.pipe(map$((stage) => stage ? stage.costCenters : []));
  unusedCostCenters$ = this.select(({ unusedCostCenters }) => unusedCostCenters);

  get stages(): CpStage[] {
    return this.value.stages;
  }

  get visibleStages(): CpStage[] {
    return this.project(queryCpVisibleStages);
  }

  get showDeleted(): boolean {
    return this.selection.showDeletedStages;
  }

  get currentStageCostCenters(): CpStageCc[] {
    const stage = this.currentStage;
    return stage ? stage.costCenters : [];
  }

  get currentStage(): Maybe<CpStage> {
    return cpStageArr.find(this.value.stages, this.selection.selectedStageId);
  }

  get focusedCell(): Maybe<FocusedCell> {
    return this.selection.focusedCell;
  }

  get firstInvalidStage(): Maybe<CpStage> {
    return find(this.value.stages, (stage) => !stage.isDeleted && isEntityInvalid(stage));
  }

  get hasNoSlab(): boolean {
    const { stages } = this.value;
    return stages.length > 0 && stages.every(({ isSlab }) => !isSlab);
  }

  get hasNoRequiredCostCenter(): boolean {
    if (this.value.contractStartBasedOn === JobContractStartBasedOnType.NominatedTasks) {
      return this.value.stages.every(({ costCenters }) => costCenters.length === 0);
    } else {
      return false;
    }
  }

  get hasNoRequiredContractStart(): boolean {
    if (this.value.contractStartBasedOn === JobContractStartBasedOnType.NominatedTasks) {
      return this.value.stages.every(({ costCenters }) => costCenters.every((cc) => !cc.isContractStart));
    } else {
      return false;
    }
  }

  get selection(): CriticalPathSelection {
    return this.value.selection;
  }

  constructor(private userContext: UserContext) {
    super(CRITICAL_PATH_DETAILS_INIT);
  }

  get invalid(): boolean {
    return isNotNil(this.firstInvalidStage)
      || this.hasNoSlab
      || this.hasNoRequiredCostCenter || this.hasNoRequiredContractStart
      || false;
  }

  build(details: CriticalPathDetails): void {
    this.setMode(this.userContext.hasPermission(BuilderPermission.Setup.Schedules.CriticalPath.EDIT)
      ? FormMode.EditMode
      : FormMode.ViewMode);

    this.setValue(details);
    this.ensureStageSelection();
    this.resetDirty();
  }

  /**
   * Selects given stage and guarantees that stage is visible.
   */
  selectStage(stage: CpStage | undefined): void {
    if (!stage) {
      return;
    }
    if (!stage.isActive && !this.selection.showDeletedStages) {
      this.setShowDeletedStages(true);
    }
    this.mergeSelection({ selectedStageId: getCpStageId(stage) });
  }

  /**
   * Selects stage and focuses on the specified cell
   */
  selectStageCell(stage: CpStage, focusedCell: FocusedCell): void {
    this.selectStage(stage);
    this.mergeSelection({ selectedStageId: focusedCell.id, focusedCell });
  }

  /**
   * Resets focus
   */
  resetFocusedCell(): void {
    this.mergeSelection({ focusedCell: void 0 });
  }

  /**
   * Changes 'Show Deleted' flag
   */
  setShowDeletedStages(showDeleted: boolean): void {
    this.mergeSelection({ showDeletedStages: showDeleted });

    this.ensureStageSelection();
  }

  /**
   * Adds stage to the beginning of stage list
   */
  addStage(stage: CpStage): void {
    this.updateStages(cpStageArr.append(this.value.stages, stage));
  }

  /**
   * Updates given stage
   */
  updateStage(stage: CpStage): void {
    this.updateStages(cpStageArr.update(this.value.stages, getCpStageId(stage), stage));
  }

  /**
   * Removes given stage. If stage comes from the backend we have to send this stage with isDeleted flag later.
   */
  deleteStage(deletedStage: CpStage): void {
    const stageId = getCpStageId(deletedStage);
    const withRemovedStage =
      deletedStage.isNew
        // remove stage if it was added during this edit session
        ? cpStageArr.remove(this.value.stages, stageId)
        // mark stage to be deleted on process
        : cpStageArr.merge(this.value.stages, stageId, (stage) => setEntityFields(cpStageToRemoved(stage), {
          isDeleted: true,
        }));
    // find all stages where deleted stage assigned as substitor and reset substitutor
    const withCleanedSubstitutor = cpStageArr.updateAll(
      withRemovedStage,
      (stage) => stage.substituteId === stageId ? setCpStageFields(stage, { substituteId: void 0 }) : stage);

    this.updateStages(withCleanedSubstitutor);
  }

  /**
   * Marks given stage as deleted
   */
  markStageAsDeleted(stage: CpStage): void {
    this.updateStages(cpStageArr.merge(
      this.value.stages,
      getCpStageId(stage),
      cpStageToRemoved));
  }

  /**
   * Removes 'Marked for deletion' flag from the given stage
   */
  undeleteStage(stage: CpStage): void {
    this.updateStages(cpStageArr.merge(
      this.value.stages,
      getCpStageId(stage),
      cpStageToRestored));
  }

  /**
   * Moves stage
   */
  moveStages(from: CpStage, to: CpStage): void {
    const { stages } = this.value;
    const fromIndex = cpStageArr.indexOf(stages, getCpStageId(from));
    const toIndex = cpStageArr.indexOf(stages, getCpStageId(to));

    this.updateStages(cpStageArr.move(stages, fromIndex, toIndex));
  }

  /**
   * Marks stage as 'Slab' and guarantees that ONLY one stage is marked as 'Slab'
   */
  setAsSlab(stage: CpStage): void {
    const currentSlab = find(this.value.stages, { isSlab: true });

    // reset current slab
    if (currentSlab) {
      this.updateStages(cpStageArr.merge(this.value.stages, getCpStageId(currentSlab), { isSlab: false }));
    }

    // set new slab
    this.updateStages(cpStageArr.merge(this.value.stages, getCpStageId(stage), { isSlab: true }));
  }

  /**
   * Sets subsitutor of the given stage
   */
  setSubstitutor(stage: CpStage, substituteId: ID): void {
    this.updateStages(cpStageArr.merge(
      this.value.stages,
      getCpStageId(stage),
      (updatedStage) => setCpStageFields(updatedStage, { substituteId })));
  }

  /**
   * Moves CC of the current stage
   */
  moveCurrentStageCc(from: CpStageCc, to: CpStageCc): void {
    const { currentStageCostCenters } = this;

    this.updateCurrentStage((stage) => {
      const fromIndex = cpStageCcArr.indexOf(currentStageCostCenters, getCpStageCcId(from));
      const toIndex = cpStageCcArr.indexOf(currentStageCostCenters, getCpStageCcId(to));

      return setEntityField(
        stage,
        'costCenters',
        cpStageCcArr.move(currentStageCostCenters, fromIndex, toIndex));
    });
  }

  /**
   * Moves CC from CP stage to list of unused CC
   */
  removeCurrentStageCc(cc: CpStageCc): void {
    this.updateCurrentStage((stage) => setEntityField(
      stage,
      'costCenters',
      cpStageCcArr.remove(stage.costCenters, getCpStageCcId(cc))));
    this.setField('unusedCostCenters', cpUnusedCcArr.insertSorted(
      this.value.unusedCostCenters,
      cpUnusedCcFromStageCc(cc),
      'code'));
  }

  /**
   * Moves CC from list of unused CC into CP stage
   */
  addCurrentStageCc(unusedCc: CpUnusedCc): void {
    const { currentStage } = this;
    if (currentStage) {
      const stageCc = cpUnusedCcToStageCc(unusedCc, currentStage);
      this.updateCurrentStage((stage) => {
        // if (!stage.costCenters.find((item) => !!item.isContractStart)) {
        //   setTimeout(() => {
        //     this.setCurrentStageContractStartCc(stageCc);
        //   });
        // }
        return setEntityField(
        stage,
        'costCenters',
        cpStageCcArr.append(stage.costCenters, stageCc));
      });
      this.setField('unusedCostCenters', cpUnusedCcArr.remove(this.value.unusedCostCenters, getCpUnusedCcId(unusedCc)));
    }
  }

  /**
   * Sets contract start flag on one of CC.
   */
  setCurrentStageContractStartCc(newContractStartCc: CpStageCc): void {
    // traverse all stages and their cost centers to reset current contract start
    this.setField(
      'stages',
      cpStageArr.updateAll(
        this.value.stages,
        (stage) => setEntityField(
          stage,
          'costCenters',
          cpStageCcArr.updateAll(stage.costCenters, (cc) => setEntityField(cc, 'isContractStart', false)))));

    // set new contract start
    this.updateCurrentStage((stage) =>
      setEntityField(
        stage,
        'costCenters',
        cpStageCcArr.merge(stage.costCenters, getCpStageCcId(newContractStartCc), { isContractStart: true })));
  }

  private updateCurrentStage(transform: IdentityFn<CpStage>): void {
    const { currentStage } = this;
    if (isNil(currentStage)) {
      return;
    }

    this.updateStages(cpStageArr.update(
      this.value.stages,
      getCpStageId(currentStage),
      transform(currentStage)));
  }

  private updateStages(stages: CpStage[]): void {
    this.startChangeDetection();
    this.setField('stages', stages);
    this.setDirtyIfChanged();
    this.ensureStageSelection();
  }

  private ensureStageSelection(): void {
    const { selectedStageId } = this.selection;

    // if stage is not selected or selected stage is not visible => select first visible stage
    if (isNil(selectedStageId) || isNil(cpStageArr.find(this.visibleStages, selectedStageId))) {
      const firstStage = first(this.visibleStages);
      if (firstStage) {
        this.mergeSelection({ selectedStageId: getCpStageId(firstStage) });
      }
    }
  }

  private mergeSelection(selection: Partial<CriticalPathSelection>): void {
    this.setField('selection', setEntityFields(this.value.selection, selection));
  }
}
